<template>
  <div data-testid="feed-field-rules-trail-row" class="feed-field-rules-trail-row">
    <!--
      One block per changed field, sourced from `record.reasons[]` (the
      writer's source of truth). Mobile-first stacked layout:

        <field label>
        <before> → <after>
        Feed source: <friendly feed>     (omitted when no source)
        Why: <explanation>               (omitted when redundant / absent)
    -->
    <ul
      v-if="changedFields.length > 0"
      class="changed-field-list"
      data-testid="feed-field-rules-trail-field-list"
    >
      <li
        v-for="(field, idx) in changedFields"
        :key="`field:${idx}:${field.label}`"
        class="changed-field"
        :data-testid="`feed-field-rules-trail-field-${idx}`"
      >
        <p class="field-label" data-testid="feed-field-rules-trail-field-label">
          {{ field.label }}
        </p>
        <p class="field-diff" data-testid="feed-field-rules-trail-field-diff">
          <span class="value value--before">{{ field.before }}</span>
          <span class="diff-arrow" aria-hidden="true">→</span>
          <span class="sr-only">changed to</span>
          <span class="value value--after">{{ field.after }}</span>
        </p>
        <p
          v-if="field.source.length > 0"
          class="field-source"
          data-testid="feed-field-rules-trail-field-source"
        >
          Feed source: {{ field.source }}
        </p>
        <p
          v-if="field.why"
          class="field-why"
          data-testid="feed-field-rules-trail-field-why"
        >
          Why: {{ field.why }}
        </p>
      </li>
    </ul>

    <!--
      Defensive fallback. Reached only when a record carries no usable
      per-field detail (e.g. a future skipped / error record, or a writer
      contract regression). Never renders the legacy condition-rule
      "resolved for this run" phrasing.
    -->
    <p
      v-else
      class="feed-field-rules-trail-fallback"
      data-testid="feed-field-rules-trail-fallback"
    >
      {{ fallbackCopy }}
    </p>
  </div>
</template>

<script lang="ts" setup>
/**
 * FeedFieldRulesDecisionTrailRow
 *
 * Renders one `feedFieldRules` decision record inside the VIN-trail
 * dialog. Pure presentational: no store reads, no fetches, no navigation.
 *
 * The actual-publish writer emits one `reasons[]` entry per field a feed
 * field rule changed, each carrying `{ configLabel, field, beforeValue,
 * afterValue, sourceAfter, transformName, explanation }`. This row treats
 * `reasons[]` as the source of truth and renders a compact, stacked block
 * per changed field.
 *
 * Anti-jargon contract (enforced by the row's test suite):
 *   - NEVER renders raw backend identifiers or technical producer names
 *     (`vauto`, `fdds2`, `feedFieldRules`, `provenance`, etc.). Feed names
 *     are humanized via `formatFeedSource`; all copy comes from the shared
 *     label helpers.
 *   - Missing before/after values collapse to a friendly sentinel.
 *   - The "Why" line is shown only when the explanation adds information
 *     beyond the field label + feed source already on screen.
 */
import { computed } from 'vue'
import {
  publishFlowFeedFieldRuleFieldLabel,
  publishFlowFeedFieldRuleValueCopy,
  publishFlowFeedFieldRuleWhyCopy,
  publishFlowFeedFieldRulesErrorCopy,
  publishFlowFeedFieldRulesGenericChangedCopy,
  publishFlowFeedFieldRulesNoRulesCopy,
} from '@/utils/publishFlowStageLabels'
import { formatFeedSource } from '@/utils/feedSourceFormat'
import type {
  PublishDecisionRecord,
  PublishDecisionReason,
} from '@/services/publishPreviewArtifactsService'

interface ChangedFieldView {
  label: string
  before: string
  after: string
  /** Humanized feed source; empty string when no usable source was captured. */
  source: string
  /** Optional "Why" explanation; null when absent or merely redundant. */
  why: string | null
}

const props = defineProps<{
  record: PublishDecisionRecord
}>()

/**
 * View-model for each changed field. Entries without a usable field label
 * are dropped so a malformed reason never renders an empty block. Writer
 * order is preserved.
 */
const changedFields = computed<ChangedFieldView[]>(() => {
  const raw = props.record.reasons
  if (!Array.isArray(raw)) return []
  const out: ChangedFieldView[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const reason = entry as PublishDecisionReason
    const label = publishFlowFeedFieldRuleFieldLabel(reason)
    if (label.length === 0) continue
    const rawSource = typeof reason.sourceAfter === 'string' ? reason.sourceAfter.trim() : ''
    out.push({
      label,
      before: publishFlowFeedFieldRuleValueCopy(reason.beforeValue),
      after: publishFlowFeedFieldRuleValueCopy(reason.afterValue),
      source: rawSource.length > 0 ? formatFeedSource(rawSource) : '',
      why: publishFlowFeedFieldRuleWhyCopy({
        explanation: reason.explanation,
        label,
        source: rawSource,
      }),
    })
  }
  return out
})

const fallbackCopy = computed<string>(() => {
  const status = typeof props.record.status === 'string' ? props.record.status : ''
  if (status === 'skipped') return publishFlowFeedFieldRulesNoRulesCopy()
  if (status === 'error') return publishFlowFeedFieldRulesErrorCopy()
  return publishFlowFeedFieldRulesGenericChangedCopy()
})
</script>

<style scoped>
.feed-field-rules-trail-row {
  /* The parent v-list-item supplies vertical rhythm. */
}
.changed-field-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.changed-field + .changed-field {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.field-label {
  margin: 0;
  font-weight: 600;
  font-size: 13px;
}
.field-diff {
  margin: 2px 0 0;
  font-size: 13px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
}
.value {
  word-break: break-word;
}
.value--before {
  color: rgba(0, 0, 0, 0.6);
}
.value--after {
  font-weight: 500;
}
.diff-arrow {
  color: rgba(0, 0, 0, 0.45);
}
.field-source,
.field-why {
  margin: 2px 0 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.feed-field-rules-trail-fallback {
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
